import React, { useRef } from 'react';
import { mergeProps, useListBox } from 'react-aria';
import MenuList from 'components/MenuList';
import { useMergedRefs } from 'hooks';
import ListBoxOption from './ListBoxOption';
import ListBoxSectionContent from './ListBoxSectionContent';
import { ListBoxData, ListBoxProps, ListBoxViewProps } from './types';

function ForwardedListBoxView<T extends ListBoxData = ListBoxData>(
  { className, state, ...props }: ListBoxViewProps<T>,
  ref: React.ForwardedRef<HTMLUListElement>,
): React.ReactElement | null {
  const localRef = useRef<HTMLUListElement>(null);
  const mergedRefs = useMergedRefs([localRef, ref]);

  const { listBoxProps } = useListBox(props, state, localRef);
  const mergedProps = mergeProps(listBoxProps, props);

  return (
    <MenuList {...mergedProps} className={className} ref={mergedRefs}>
      {Array.from(state.collection).map((item) => {
        switch (item.type) {
          case 'section':
            return (
              <ListBoxSectionContent
                key={item.key}
                section={item}
                state={state}
              />
            );
          default:
            return <ListBoxOption key={item.key} item={item} state={state} />;
        }
      })}
    </MenuList>
  );
}

/**
 * Accessible list-box component, useful when giving the user a list of options
 * to select.
 *
 * The `ListBox` needs `state` to be passed in. The `ListBox` can be used in
 * various use cases, which means that state often comes from different places.
 *
 * For example, when using the `ListBox` in a select menu, the state would come from
 * react-aria's `useSelectState`.  When using the `ListBox` as a generic list-box,
 * the state would come from `useListState`
 *
 * For convenience, the `useListState` react-stately hook is exported from this
 * module as `useListBoxState`
 */
const ListBoxView = React.forwardRef(ForwardedListBoxView) as <
  T extends ListBoxData = ListBoxData,
>(
  props: ListBoxProps<T> & { ref?: React.Ref<HTMLUListElement> },
) => React.ReactElement | null;

export default ListBoxView;
